import { type AriaProps } from '@common/components/controls/ariaHelpers/types';

/**
 * State for generating form control ARIA attributes.
 * Supports Input, Checkbox, Radio, Toggle components.
 */
export interface FormControlState {
  invalid?: boolean;
  errorId?: string;
  disabled?: boolean;
  checked?: boolean | 'mixed';
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
}

/**
 * Generates ARIA attributes for form control elements.
 * Handles validation state and error linking.
 *
 * @param state - The current form control state
 * @returns ARIA props to spread onto the form control element
 */
export function getFormControlAriaProps(state: FormControlState): AriaProps {
  const {
    invalid,
    errorId,
    disabled,
    checked,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
  } = state;

  const props: AriaProps = {};

  // Validation state
  if (invalid) {
    props['aria-invalid'] = true;

    // Link to error message
    if (errorId) {
      const describedBy = ariaDescribedBy
        ? `${ariaDescribedBy} ${errorId}`
        : errorId;
      props['aria-describedby'] = describedBy;
    }
  } else if (ariaDescribedBy) {
    props['aria-describedby'] = ariaDescribedBy;
  }

  // Disabled state
  if (disabled) {
    props['aria-disabled'] = true;
  }

  // Checked state for checkbox/radio/toggle
  if (checked !== undefined) {
    props['aria-checked'] = checked;
  }

  // Labeling
  if (ariaLabel) {
    props['aria-label'] = ariaLabel;
  } else if (ariaLabelledBy) {
    props['aria-labelledby'] = ariaLabelledBy;
  }

  return props;
}

/**
 * Generates unique ID for form control error message element.
 *
 * @param controlId - The ID of the form control element
 * @returns ID string for the error message element
 */
export function getFormControlErrorId(controlId: string): string {
  return `${controlId}-error`;
}

/**
 * Checks if form control should show error state.
 * Error should only be visible and announced after user interaction.
 *
 * @param error - Error message if any
 * @param touched - Whether user has interacted with control
 * @param showError - Whether to show error (form submission attempted)
 * @returns Boolean indicating if error should be shown
 */
export function shouldShowError(
  error?: string,
  touched?: boolean,
  showError?: boolean,
): boolean {
  return !!(showError && touched && error);
}
